import { defineStore } from 'pinia';

type DownloadStatus =
  | 'pending'
  | 'generating'
  | 'generateSuccess'
  | 'downloading'
  | 'generateError'
  | 'downloadSuccess'
  | 'downloadError'
  | 'highDefinitionModelGenerating'
  | 'highDefinitionModelGenerateSuccess'
  | 'highDefinitionModelGenerateError';

/**
 * 下载类型：用于区分不同类型的下载，影响水印 bar 等 UI 的显示
 * - image: 图片下载（可触发水印引导）
 * - psd / video / other: 源文件类下载（跳过水印引导；other 含 3D glb/obj 等）
 */
type DownloadType = 'image' | 'psd' | 'video' | 'other';

/**
 * 下载状态管理 Store
 * 用于管理图片生成和下载的全局状态
 *
 * 状态流转：
 * 生图流程: pending -> generating -> generateSuccess -> downloading -> downloadSuccess/downloadError
 * 画板导出: 直接调用 addDownload() 进入 generateSuccess 状态 -> downloadSuccess/downloadError
 */
export const useDownloadImageStore = defineStore('downloadImage', {
  state: () => ({
    total: 0,
    pendingNumber: 0, // 等待处理
    generatingNumber: 0, // 正在处理
    generateSuccessNumber: 0, // 处理完成等待下载
    downloadingNumber: 0, // 正在下载中
    generateErrorNumber: 0, // 处理失败
    downloadSuccessNumber: 0, // 下载完成
    downloadErrorNumber: 0, // 下载失败
    activeStatus: 'pending' as DownloadStatus,
    timer: null as NodeJS.Timeout | null,
    // 记录已创建的 URL 对象，用于页面卸载时清理
    objectURLs: [] as string[],
    // 当前下载类型，用于区分不同类型的下载（影响水印 bar 等 UI 显示）
    downloadType: 'image' as DownloadType,
  }),
  actions: {
    /**
     * 添加下载任务（直接下载，无需生成过程）
     * 跳过生成阶段，直接进入 downloading 状态
     * @param type 下载类型，默认为 'image'
     */
    addDownload(type: DownloadType = 'image') {
      this.total++;
      this.downloadingNumber++;
      this.activeStatus = 'downloading';
      this.downloadType = type;
    },
    /**
     * 添加生图任务（需要先生成图片）
     * 生图流程: pending -> generating -> generateSuccess -> download
     */
    addGenerate() {
      this.total++;
      this.pendingNumber++;
      this.activeStatus = 'pending';
    },
    /**
     * 注册 Object URL，用于页面卸载时统一清理
     * @param url - createObjectURL 创建的 URL
     */
    registerObjectURL(url: string) {
      this.objectURLs.push(url);
    },
    /**
     * 清理所有注册的 Object URL
     * 在页面卸载或状态重置时调用，防止内存泄漏
     */
    cleanupObjectURLs() {
      this.objectURLs.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // 忽略已失效的 URL
        }
      });
      this.objectURLs = [];
    },
    updateDownload(status: DownloadStatus) {
      this.timer && clearTimeout(this.timer);
      this.activeStatus = status;

      switch (status) {
        case 'generating':
          this.pendingNumber = Math.max(0, this.pendingNumber - 1);
          this.generatingNumber++;
          break;
        case 'generateSuccess':
          this.generatingNumber = Math.max(0, this.generatingNumber - 1);
          this.generateSuccessNumber++;
          break;
        case 'downloading':
          this.generateSuccessNumber = Math.max(0, this.generateSuccessNumber - 1);
          this.downloadingNumber++;
          break;
        case 'generateError':
          this.generatingNumber = Math.max(0, this.generatingNumber - 1);
          this.generateErrorNumber++;
          break;
        case 'downloadSuccess':
          this.downloadingNumber = Math.max(0, this.downloadingNumber - 1);
          this.generateSuccessNumber = Math.max(0, this.generateSuccessNumber - 1);
          this.downloadSuccessNumber++;
          break;
        case 'downloadError':
          this.downloadingNumber = Math.max(0, this.downloadingNumber - 1);
          this.generateSuccessNumber = Math.max(0, this.generateSuccessNumber - 1);
          this.downloadErrorNumber++;
          break;
        // 高清模型处理中
        case 'highDefinitionModelGenerating':
          this.generatingNumber = Math.max(0, this.generatingNumber - 1);
          this.generateSuccessNumber++;
          break;
        // 高清模型处理完成
        case 'highDefinitionModelGenerateSuccess':
          this.generateSuccessNumber = Math.max(0, this.generateSuccessNumber - 1);
          this.downloadSuccessNumber++;
          break;
        // 高清模型处理失败
        case 'highDefinitionModelGenerateError':
          this.generateSuccessNumber = Math.max(0, this.generateSuccessNumber - 1);
          this.generateErrorNumber++;
          break;
      }

      if ((this.generateErrorNumber + this.downloadSuccessNumber + this.downloadErrorNumber) >= this.total) {
        // 所有任务完成，延迟重置状态
        // 使用较长的延迟时间，确保用户能看到最终状态
        this.timer = setTimeout(() => {
          this.initState();
        }, 2000); // 2秒后重置，给用户足够的反馈时间
      }
    },
    /**
     * 初始化/重置状态
     * 清理所有计数器和定时器，释放 Object URL
     */
    initState() {
      // 清理定时器
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      // 清理 Object URL，防止内存泄漏
      this.cleanupObjectURLs();
      // 重置所有计数器
      this.total = 0;
      this.pendingNumber = 0;
      this.generatingNumber = 0;
      this.generateSuccessNumber = 0;
      this.downloadingNumber = 0;
      this.generateErrorNumber = 0;
      this.downloadErrorNumber = 0;
      this.downloadSuccessNumber = 0;
      this.activeStatus = 'pending';
      this.downloadType = 'image';
    },
  },
  persist: {
    key: 'pinoDownloadImageStore',
    // 不持久化任何状态，防止刷新后残留的下载状态误触发 Snackbar 等逻辑
    paths: [],
  },
});